import React, { useState, useEffect, useCallback, useRef } from "react";
import {
    Box, Typography, Card, CardContent,
    CircularProgress, Alert, Button,
} from "@mui/material";
import {
    TrendingUp, AssignmentLate, Percent, CallOutlined,
    ChatOutlined, Handshake, EventRepeat, Insights, EmojiEvents, AttachMoney,
} from "@mui/icons-material";
import { Link as RouterLink } from "react-router-dom";
import { WB_SX, WB_UI } from "../../../ui/ux/uiTokens";
import CRMStatCard from "../common/CRMStatCard";
import CRMBadge from "../common/CRMBadge";
import axiosInstance from "../../../api/axiosInstance";
import base_url from "../../../constants";
import useNotification from "../../common/Notification";
import {
    fmtValue,
    DEAL_STATUS_META,
    formatWindowDate,
    normalizeDealStatusCounts,
} from "../common/crmAnalyticsUtils";

const taskTypeIcon = (type) =>
    type === "call" ? <CallOutlined fontSize="small" />
    : type === "message" ? <ChatOutlined fontSize="small" />
    : type === "follow-up" ? <EventRepeat fontSize="small" />
    : <Handshake fontSize="small" />;

const STAGE_COLORS = [
    { bg: "#eef2ff", border: "#c7d2fe", text: "#3730a3" },
    { bg: "#eff6ff", border: "#bfdbfe", text: "#1d4ed8" },
    { bg: "#fefce8", border: "#fde68a", text: "#854d0e" },
    { bg: "#fff1f2", border: "#fecdd3", text: "#be123c" },
    { bg: "#ecfdf5", border: "#a7f3d0", text: "#065f46" },
];

const CRMDashboard = () => {
    const _notify = useNotification();
    const notifyRef = useRef(_notify);
    notifyRef.current = _notify;

    const [stats, setStats] = useState(null);
    const [pipeline, setPipeline] = useState([]);
    const [tasksToday, setTasksToday] = useState({ overdue: [], due_today: [] });
    const [dealStatus, setDealStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchAll = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [statsRes, pipelineRes, tasksRes, dealStatusRes] = await Promise.all([
                axiosInstance.get(`${base_url}/crm/dashboard`),
                axiosInstance.get(`${base_url}/crm/dashboard/pipeline`),
                axiosInstance.get(`${base_url}/crm/dashboard/tasks-today`),
                axiosInstance.get(`${base_url}/crm/dashboard/deal-status-counts`, {
                    params: { months: 1 },
                }).catch(() => ({ data: {} })),
            ]);
            setStats(statsRes.data?.data ?? statsRes.data);

            const pipelineRaw = pipelineRes.data?.data ?? pipelineRes.data;
            setPipeline(Array.isArray(pipelineRaw) ? pipelineRaw : []);

            const tasksRaw = tasksRes.data?.data ?? tasksRes.data;
            setTasksToday(tasksRaw ?? { overdue: [], due_today: [] });

            const statusRaw = dealStatusRes.data?.data ?? dealStatusRes.data;
            setDealStatus(statusRaw && (statusRaw.deals_total != null || statusRaw.deals_open != null)
                ? normalizeDealStatusCounts(dealStatusRes.data)
                : null);
        } catch (err) {
            const msg = err?.response?.data?.message || "Failed to load dashboard data";
            setError(msg);
            notifyRef.current(msg, "error");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchAll(); }, [fetchAll]);

    const today = new Date().toLocaleDateString("en-IN", {
        weekday: "long", day: "numeric", month: "long", year: "numeric",
    });

    const todayTasksAll = [
        ...(tasksToday.overdue || []).map((t) => ({ ...t, _isOverdue: true })),
        ...(tasksToday.due_today || []),
    ];

    const dealStatusTotal = dealStatus?.deals_total || 0;
    const dealStatusSegs = dealStatus ? [
        { key: "open", value: dealStatus.deals_open, ...DEAL_STATUS_META.open },
        { key: "won",  value: dealStatus.deals_won,  ...DEAL_STATUS_META.won },
        { key: "lost", value: dealStatus.deals_lost, ...DEAL_STATUS_META.lost },
    ] : [];

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 300 }}>
                <CircularProgress size={32} />
            </Box>
        );
    }

    if (error && !stats) {
        return <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>;
    }

    return (
        <Box sx={WB_SX.page}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2, mb: 3, flexWrap: "wrap" }}>
                <Box>
                    <Typography variant="h5" sx={{ ...WB_SX.pageTitle, mb: 0.5 }}>
                        CRM Dashboard
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {today}
                        {stats && ` · ${stats.tasks_overdue || 0} tasks overdue`}
                    </Typography>
                </Box>
                <Button
                    component={RouterLink}
                    to="/crm/analytics"
                    size="small"
                    variant="outlined"
                    startIcon={<Insights />}
                    sx={{ textTransform: "none", fontWeight: 600, borderColor: WB_UI.color.border, color: "text.primary" }}
                >
                    View analytics
                </Button>
            </Box>

            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 3 }}>
                <CRMStatCard
                    label="Pipeline Value"
                    value={fmtValue(stats?.deals_open_value)}
                    sub={`${stats?.deals_open ?? "—"} open deals`}
                    accent="#1a2333"
                    icon={<TrendingUp fontSize="small" />}
                />
                <CRMStatCard
                    label="Won Deals"
                    value={stats?.deals_won ?? "—"}
                    sub={`Lost: ${stats?.deals_lost ?? "—"}`}
                    accent="#065f46"
                    icon={<EmojiEvents fontSize="small" />}
                />
                <CRMStatCard
                    label="Total Deals"
                    value={stats?.deals_total ?? "—"}
                    sub={`Open: ${stats?.deals_open ?? "—"}`}
                    accent="#1d4ed8"
                    icon={<AttachMoney fontSize="small" />}
                />
                <CRMStatCard
                    label="Tasks Pending"
                    value={stats?.tasks_pending ?? "—"}
                    sub={`Completed: ${stats?.tasks_completed ?? "—"}`}
                    accent="#b45309"
                    icon={<Percent fontSize="small" />}
                />
                <CRMStatCard
                    label="Tasks Overdue"
                    value={stats?.tasks_overdue ?? "—"}
                    sub={`Total tasks: ${stats?.tasks_total ?? "—"}`}
                    accent="#be123c"
                    icon={<AssignmentLate fontSize="small" />}
                />
            </Box>

            {dealStatus && (
                <Card elevation={0} sx={{ ...WB_SX.card, mb: WB_UI.spacing.sectionGap }}>
                    <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1, mb: 1.5 }}>
                            <Box>
                                <Typography variant="subtitle1" sx={WB_SX.sectionTitle}>
                                    Deal outcomes · last month
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {dealStatus.from && dealStatus.to
                                        ? `${formatWindowDate(dealStatus.from)} – ${formatWindowDate(dealStatus.to)}`
                                        : "Deals created in the last 1 month"}
                                    {` · ${dealStatusTotal} total`}
                                </Typography>
                            </Box>
                            <Button
                                component={RouterLink}
                                to="/crm/analytics"
                                size="small"
                                sx={{ textTransform: "none", fontWeight: 600, minWidth: 0 }}
                            >
                                Details
                            </Button>
                        </Box>

                        {dealStatusTotal === 0 ? (
                            <Typography variant="body2" color="text.disabled" sx={{ py: 1.5, textAlign: "center" }}>
                                No deals created in the last month
                            </Typography>
                        ) : (
                            <>
                                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 1.5 }}>
                                    {dealStatusSegs.map((seg) => (
                                        <Box key={seg.key} sx={{ minWidth: 88 }}>
                                            <Typography variant="caption" sx={{ color: seg.color, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase" }}>
                                                {seg.label}
                                            </Typography>
                                            <Typography variant="h6" sx={{ fontWeight: 800, color: seg.color, lineHeight: 1.2 }}>
                                                {seg.value}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Box>
                                <Box sx={{ display: "flex", height: 10, borderRadius: 999, overflow: "hidden", backgroundColor: "rgba(0,0,0,0.06)" }}>
                                    {dealStatusSegs.filter((s) => s.value > 0).map((seg) => (
                                        <Box
                                            key={seg.key}
                                            title={`${seg.label}: ${seg.value}`}
                                            sx={{
                                                width: `${(seg.value / dealStatusTotal) * 100}%`,
                                                backgroundColor: seg.color,
                                            }}
                                        />
                                    ))}
                                </Box>
                            </>
                        )}
                    </CardContent>
                </Card>
            )}

            {pipeline.length > 0 && (
                <Card elevation={0} sx={{ ...WB_SX.card, mb: WB_UI.spacing.sectionGap }}>
                    <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
                        <Typography variant="subtitle1" sx={{ ...WB_SX.sectionTitle, mb: 2 }}>
                            Pipeline by Stage
                        </Typography>
                        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                            {pipeline.map((stage, i) => {
                                const c = STAGE_COLORS[i % STAGE_COLORS.length];
                                return (
                                    <Box
                                        key={stage.pipeline_stage_id || i}
                                        sx={{
                                            flex: 1, minWidth: 100,
                                            backgroundColor: c.bg,
                                            border: `1px solid ${c.border}`,
                                            borderRadius: 2, p: 1.75,
                                        }}
                                    >
                                        <Typography variant="caption" sx={{ fontWeight: 700, color: c.text, letterSpacing: "0.05em", textTransform: "uppercase", display: "block" }}>
                                            {stage.stage_name}
                                        </Typography>
                                        <Typography variant="h6" sx={{ fontWeight: 800, color: c.text, mt: 0.25 }}>
                                            {stage.deals_count ?? 0}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: c.text, opacity: 0.8 }}>
                                            {fmtValue(stage.total_value)}
                                        </Typography>
                                    </Box>
                                );
                            })}
                        </Box>
                    </CardContent>
                </Card>
            )}

            <Card elevation={0} sx={WB_SX.card}>
                <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2, gap: 1, flexWrap: "wrap" }}>
                        <Typography variant="subtitle1" sx={WB_SX.sectionTitle}>
                            Today&apos;s tasks
                        </Typography>
                        <Box sx={{ display: "flex", gap: 0.75 }}>
                            {tasksToday.overdue_count > 0 && (
                                <CRMBadge label={`${tasksToday.overdue_count} overdue`} bg="#fee2e2" text="#b91c1c" />
                            )}
                            {tasksToday.due_today_count > 0 && (
                                <CRMBadge label={`${tasksToday.due_today_count} due`} bg="#dbeafe" text="#1d4ed8" />
                            )}
                        </Box>
                    </Box>

                    {todayTasksAll.length === 0 ? (
                        <Typography variant="body2" color="text.disabled" sx={{ py: 2, textAlign: "center" }}>
                            No tasks for today
                        </Typography>
                    ) : (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
                            {todayTasksAll.map((t) => (
                                <Box
                                    key={t.id}
                                    sx={{
                                        display: "flex", alignItems: "center", gap: 1.5,
                                        p: 1.25, borderRadius: 1.5,
                                        backgroundColor: t._isOverdue ? "rgba(220,38,38,0.05)" : "rgba(0,0,0,0.02)",
                                        border: "1px solid",
                                        borderColor: t._isOverdue ? "rgba(220,38,38,0.12)" : WB_UI.color.border,
                                    }}
                                >
                                    <Box sx={{ color: t._isOverdue ? "error.main" : "text.secondary" }}>
                                        {taskTypeIcon(t.type)}
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{t.title}</Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {t.deal?.title || t.deal?.contact?.name || ""}
                                            {t._isOverdue && t.due_date ? ` · ${new Date(t.due_date).toLocaleDateString("en-IN")}` : ""}
                                            {t.assign_user ? ` · ${t.assign_user}` : ""}
                                        </Typography>
                                    </Box>
                                    <CRMBadge
                                        label={t._isOverdue ? "Overdue" : "Today"}
                                        bg={t._isOverdue ? "#fee2e2" : "#f3f4f6"}
                                        text={t._isOverdue ? "#b91c1c" : "#374151"}
                                    />
                                </Box>
                            ))}
                        </Box>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
};

export default CRMDashboard;
